import type { Dispatch, SetStateAction } from 'react';
import type { TFunction } from 'i18next';
import type { User } from '@/types/api';
import type { Establishment, NominalsFilters, UsersFilters } from '@/types/nominals';

export type AddressBookTabId = 'personal' | 'shared';

export type AddressBookContactModalTab = 'personal' | 'shared';

export type AddressBookContactModalState = {
  mode: 'create' | 'edit';
  tab: AddressBookContactModalTab;
  contactId?: string;
} | null;

export type AddressBookPaginated<T> = {
  count: number;
  results: T[];
  next?: string | null;
  previous?: string | null;
};

export type AddressBookTabConfig = {
  id: AddressBookTabId;
  labelKey: string;
  icon: string;
};

export type AddressBookPendingDelete = {
  tab: AddressBookContactModalTab;
  contactId: string;
} | null;

export const ADDRESS_BOOK_CONTACTS_GRID_CLASS = 'grid grid-cols-[1fr_1fr_3rem] gap-8';
export const ADDRESS_BOOK_DEFAULT_PAGE_FILTERS = { page: 1, page_size: 20 };

export const NOMINALS_TAB_ICON = 'user-lock';
export const USERS_TAB_ICON = 'user-tag';

export const ADDRESS_BOOK_TABS: AddressBookTabConfig[] = [
  { id: 'personal', labelKey: 'addressBook.personalContacts', icon: 'user-check' },
  { id: 'shared', labelKey: 'addressBook.sharedContacts', icon: 'people-group' },
];

export const ADDRESS_BOOK_SHARED_CONTACT_TYPES: { value: string; labelKey: string }[] = [
  { value: 'SUPPLIER', labelKey: 'contact.supplier' },
  { value: 'GOVERNMENT', labelKey: 'contact.government' },
  { value: 'EMERGENCY', labelKey: 'contact.emergencyService' },
  { value: 'MEDICAL', labelKey: 'contact.medical' },
  { value: 'LEGAL', labelKey: 'contact.legal' },
  { value: 'MAINTENANCE', labelKey: 'contact.maintenance' },
  { value: 'SECURITY', labelKey: 'contact.security' },
  { value: 'OTHER', labelKey: 'contact.other' },
];

export const ADDRESS_BOOK_USERS_TABLE_GRID =
  'grid grid-cols-[minmax(180px,1.2fr)_minmax(200px,1.2fr)_minmax(180px,1fr)_minmax(90px,0.7fr)_minmax(140px,1fr)] gap-6';

export const ADDRESS_BOOK_NOMINALS_TABLE_GRID =
  'grid grid-cols-[minmax(180px,1.2fr)_minmax(160px,0.9fr)_minmax(180px,1fr)_minmax(140px,1fr)] gap-6';

export function resolveContactCountryCode(
  establishments: Establishment[],
  establishmentId: number | null | undefined,
  userEstablishment: User['establishment']
): string | undefined {
  const id = establishmentId ?? userEstablishment?.id;
  if (userEstablishment?.country_code) {
    return userEstablishment.country_code;
  }
  if (!id) {
    return undefined;
  }
  return establishments.find(e => e.id === id)?.country_code;
}

export function addressBookPageChangeHandler<T extends { page?: number }>(
  setFilters: Dispatch<SetStateAction<T>>
) {
  return (page: number) => {
    if (page >= 1) {
      setFilters(prev => ({ ...prev, page }));
    }
  };
}

export function addressBookPageSizeChangeHandler<
  T extends { page?: number; page_size?: number },
>(setFilters: Dispatch<SetStateAction<T>>) {
  return (size: number) => {
    setFilters(prev => ({ ...prev, page_size: size, page: 1 }));
  };
}

export function loadAddressBookPaginated<T>(
  setLoading: (loading: boolean) => void,
  setError: (error: string | null) => void,
  setData: (data: AddressBookPaginated<T> | null) => void,
  onCount: (count: number) => void,
  load: () => Promise<{
    count?: number;
    results?: T[];
    next?: string | null;
    previous?: string | null;
  }>,
  errorMessage: string
) {
  setLoading(true);
  setError(null);
  return load()
    .then(data => {
      const normalized: AddressBookPaginated<T> = {
        count: data.count || 0,
        results: Array.isArray(data.results) ? data.results : [],
        next: data.next ?? null,
        previous: data.previous ?? null,
      };
      setData(normalized);
      onCount(normalized.count);
    })
    .catch(() => {
      setError(errorMessage);
      setData({ count: 0, results: [], next: null, previous: null });
    })
    .finally(() => setLoading(false));
}

export function addressBookEmptyDescription(
  search: string | undefined,
  t: TFunction,
  noSearchKey: string
) {
  return search ? t('nominals.tryAdjustingSearch') : t(noSearchKey);
}

export function addressBookUserEstablishmentRole(
  establishment: string | undefined,
  role: string | undefined
): string {
  if (establishment && role) {
    return `${establishment} / ${role}`;
  }
  return establishment || role || '—';
}

export function validateSubmitFields(
  fields: Array<{ validateForSubmit: () => boolean }>
): boolean {
  return fields.every(field => field.validateForSubmit());
}

export function buildAddressBookContactPayload(
  firstName: string,
  lastName: string,
  phone: string,
  contactType: string,
  countryCode?: string,
  friendlyName?: string
) {
  return {
    first_name: firstName,
    last_name: lastName,
    phone_number: phone,
    contact_type: contactType,
    friendly_name: (friendlyName ?? '').trim(),
    ...(countryCode ? { country_code: countryCode } : {}),
  };
}

export function createDefaultUsersFilters(): UsersFilters {
  return {
    ...ADDRESS_BOOK_DEFAULT_PAGE_FILTERS,
    is_active: true,
  };
}

export function createDefaultNominalsFilters(): NominalsFilters {
  return {
    ...ADDRESS_BOOK_DEFAULT_PAGE_FILTERS,
    is_active: true,
  };
}
